// Per-column filters for the app's tables.
//
// A filter spec is { field, kind }: "text" matches rows whose value contains
// the typed text (ignoring case); "select" matches any of the values picked
// from what the rows hold. `filters` maps field -> the typed text, or the
// array of picked values; an empty one does not filter.

// The "select" choice for rows with no value in that column.
export const NOT_SET = "__not_set__";

// The choices for a "select" column: every value the rows hold, sorted, then
// "Not set" when some rows have none. With `sorted` false they keep the order
// they first appear in - for values like "30 Sep" / "1 Oct" that do not sort
// as text.
export const filterOptions = (rows, field, { sorted = true } = {}) => {
  const values = new Set();
  let hasBlank = false;
  for (const row of rows) {
    const value = String(row[field] ?? "").trim();
    if (value) values.add(value);
    else hasBlank = true;
  }
  const ordered = sorted
    ? [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }))
    : [...values];
  const options = ordered.map((value) => ({ value, label: value }));
  return hasBlank ? [...options, { value: NOT_SET, label: "Not set" }] : options;
};

// A select filter's picked values as an array. A lone string - how a single
// pick used to be kept - reads as one pick.
export const pickedValues = (value) => {
  if (Array.isArray(value)) return value;
  return String(value ?? "").trim() === "" ? [] : [value];
};

// True when the filter narrows the rows: some text typed, or a value picked.
export const isFilterActive = (value) =>
  Array.isArray(value) ? value.length > 0 : String(value ?? "").trim() !== "";

export const matchesColumnFilters = (row, filters, specs) =>
  specs.every(({ field, kind }) => {
    const wanted = filters[field];
    if (!isFilterActive(wanted)) return true;
    const value = String(row[field] ?? "").trim();
    if (kind === "select") return pickedValues(wanted).includes(value === "" ? NOT_SET : value);
    return value.toLowerCase().includes(String(wanted).trim().toLowerCase());
  });

// How many columns are filtered, for the "Clear filters" button.
export const activeFilterCount = (filters) => Object.values(filters).filter(isFilterActive).length;
